/**
 * Authentication Service — Supabase Auth
 *
 * Replaces: Old Rails-based auth flow (LMSAdminInstance → /login, /session)
 * New path:  Supabase Auth API (signIn, updateUser) + users table (profile update)
 *
 * Hooks:
 * - useAuthentication: login/logout via Supabase Auth
 * - useProfile / useRootProfile: profile updates via Supabase users table
 * - usePasswordUpdate / useRootPasswordUpdate: password via Supabase Auth API
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { roleVar } from "@coursemanagementVars";
import { useAuth, type UserProfile } from "../../context/AuthContext";
import { supabase } from "../../services/supabase";
import { updateUserProfile } from "../../services/shared/userProfile";
import { getErrorMessage } from "../../utils/helper";

// ---------------------------------------------------------------------------
// useAuthentication — Login / Logout
// ---------------------------------------------------------------------------

export interface LoginBody {
  email?: string;
  password?: string;
}

export const useAuthentication = (body?: LoginBody) => {
  const [data, setData] = useState<LoginBody>(body ?? {});
  const [processing, setProcessing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { signIn, signOut, profile } = useAuth();
  const navigate = useNavigate();

  const setFieldData = useCallback(
    (field: string, value: any) => setData(prev => ({ ...prev, [field]: value })),
    [],
  );

  const clearErrors = useCallback(() => setErrors({}), []);

  const login = useCallback(
    async (callbacks?: { onSuccess?: (data: unknown) => void; onError?: (errors: Record<string, string>) => void }) => {
      if (!data.email || !data.password) {
        setErrors({ email: "Email and password required" });
        return;
      }
      setProcessing(true);
      setErrors({});
      try {
        const { error } = await signIn(data.email, data.password);
        if (error) {
          const msg = error.message ?? "Login failed";
          setErrors({ password: msg });
          callbacks?.onError?.({ error: msg });
        } else {
          // Profile auto-fetched by AuthContext on SIGNED_IN event
          // Navigate after a short delay to let profile load
          setTimeout(() => {
            const role = profile?.base_role;
            const ROLES = [
              "teacher",
              "student",
              "registrar",
              "parent",
              "parent-teacher",
              "admin",
            ] as const;
            if (role && (ROLES as readonly string[]).includes(role)) {
              roleVar(role as (typeof ROLES)[number]);
              navigate(`/${role}`);
            } else if (role) {
              // roles outside the roleVar union (e.g. super-admin) still navigate
              navigate(`/${role}`);
            }
          }, 100);
          callbacks?.onSuccess?.({ success: true });
        }
      } catch (err) {
        const msg = getErrorMessage(err, "Login failed");
        setErrors({ password: msg });
        callbacks?.onError?.({ error: msg });
      } finally {
        setProcessing(false);
      }
    },
    [data, signIn, navigate, profile],
  );

  const logout = useCallback(async () => {
    try {
      await signOut();
    } finally {
      // Always navigate away, even if signOut() rejected — otherwise a
      // failed remote sign-out leaves the user stuck on the current page.
      navigate("/");
    }
  }, [signOut, navigate]);

  return {
    data,
    setData,
    setFieldData,
    processing,
    errors,
    setErrors,
    clearErrors,
    response: null,
    login,
    logout,
  };
};

// ---------------------------------------------------------------------------
// useProfile — Update regular user profile (Supabase users table)
// ---------------------------------------------------------------------------

export interface ProfileBody {
  id_number?: string;
  user_name?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  /** Public URL from the `avatars` bucket; `null` = no photo / cleared. */
  avatar_url?: string | null;
}

export const useProfile = () => {
  const { profile, updateProfile } = useAuth();
  const [data, setData] = useState<ProfileBody>({
    email: profile?.email ?? "",
    first_name: profile?.first_name ?? "",
    last_name: profile?.last_name ?? "",
    id_number: profile?.details?.id_number ?? "",
    user_name: profile?.details?.user_name ?? "",
    avatar_url: profile?.avatar_url ?? null,
  });
  const [processing, setProcessing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // `profile` is null on first render and populated asynchronously by
  // AuthContext once the public.users row loads. The useState initializer above
  // only runs once, so a form that mounts before the profile arrives (e.g.
  // /:role/profile/edit opened straight after login) would keep empty name
  // fields forever. Re-sync the form whenever the underlying profile fields
  // change. Deps are primitives (never the profile object) so this can't loop,
  // and the fields are stable while the user is editing so their input is not
  // clobbered — it only syncs on load and (harmlessly, to the same values)
  // after a save.
  useEffect(() => {
    setData({
      email: profile?.email ?? "",
      first_name: profile?.first_name ?? "",
      last_name: profile?.last_name ?? "",
      id_number: profile?.details?.id_number ?? "",
      user_name: profile?.details?.user_name ?? "",
      avatar_url: profile?.avatar_url ?? null,
    });
  }, [
    profile?.id,
    profile?.email,
    profile?.first_name,
    profile?.last_name,
    profile?.details?.id_number,
    profile?.details?.user_name,
    profile?.avatar_url,
  ]);

  const setFieldData = useCallback(
    (field: string, value: any) => setData(prev => ({ ...prev, [field]: value })),
    [],
  );

  const update = useCallback(
    async (callbacks?: { onSuccess?: (data: unknown) => void; onError?: (errors: Record<string, string>) => void }) => {
      if (!profile?.id) return;
      setProcessing(true);
      setErrors({});
      try {
        const { data: updated, error } = await updateUserProfile(profile.id, {
          first_name: data.first_name,
          last_name: data.last_name,
          avatar_url: data.avatar_url ?? null,
        });
        if (error) throw error;
        updateProfile(updated as unknown as Partial<UserProfile>);
        callbacks?.onSuccess?.(updated);
      } catch (err) {
        const msg = getErrorMessage(err, "Update failed");
        setErrors({ general: msg });
        callbacks?.onError?.({ general: msg });
      } finally {
        setProcessing(false);
      }
    },
    [data, profile?.id, updateProfile],
  );

  return { data, setData, setFieldData, processing, errors, setErrors, response: null, update };
};

// ---------------------------------------------------------------------------
// useRootProfile — Update superAdmin profile (same users table)
// ---------------------------------------------------------------------------

export interface RootProfileBody {
  username?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  /** Public URL from the `avatars` bucket; `null` = no photo / cleared. */
  avatar_url?: string | null;
}

export const useRootProfile = () => {
  const { profile, updateProfile } = useAuth();
  const [data, setData] = useState<RootProfileBody>({
    email: profile?.email ?? "",
    first_name: profile?.first_name ?? "",
    last_name: profile?.last_name ?? "",
    username: profile?.user_name ?? "",
    avatar_url: profile?.avatar_url ?? null,
  });
  const [processing, setProcessing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Same profile-arrives-after-mount re-sync as useProfile (see the note there):
  // seed the form once the async profile loads, without clobbering edits.
  useEffect(() => {
    setData({
      email: profile?.email ?? "",
      first_name: profile?.first_name ?? "",
      last_name: profile?.last_name ?? "",
      username: profile?.user_name ?? "",
      avatar_url: profile?.avatar_url ?? null,
    });
  }, [
    profile?.id,
    profile?.email,
    profile?.first_name,
    profile?.last_name,
    profile?.user_name,
    profile?.avatar_url,
  ]);

  const setFieldData = useCallback(
    (field: string, value: any) => setData(prev => ({ ...prev, [field]: value })),
    [],
  );

  const update = useCallback(
    async (callbacks?: { onSuccess?: (data: unknown) => void; onError?: (errors: Record<string, string>) => void }) => {
      if (!profile?.id) return;
      setProcessing(true);
      setErrors({});
      try {
        const { data: updated, error } = await updateUserProfile(profile.id, {
          first_name: data.first_name,
          last_name: data.last_name,
          avatar_url: data.avatar_url ?? null,
        });
        if (error) throw error;
        updateProfile(updated as unknown as Partial<UserProfile>);
        callbacks?.onSuccess?.(updated);
      } catch (err) {
        const msg = getErrorMessage(err, "Update failed");
        setErrors({ general: msg });
        callbacks?.onError?.({ general: msg });
      } finally {
        setProcessing(false);
      }
    },
    [data, profile?.id, updateProfile],
  );

  return { data, setData, setFieldData, processing, errors, setErrors, response: null, update };
};

// ---------------------------------------------------------------------------
// usePasswordUpdate — Change password via Supabase Auth API
// ---------------------------------------------------------------------------

export interface PasswordBody {
  password?: string;
  current_password?: string;
  password_confirmation?: string;
}

export const usePasswordUpdate = () => {
  const [data, setData] = useState<PasswordBody>({});
  const [processing, setProcessing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const setFieldData = useCallback(
    (field: string, value: any) => setData(prev => ({ ...prev, [field]: value })),
    [],
  );

  const update = useCallback(
    async (callbacks?: { onSuccess?: (data: unknown) => void; onError?: (errors: Record<string, string>) => void }) => {
      if (!data.password) { setErrors({ password: "Password required" }); return; }
      setProcessing(true);
      setErrors({});
      try {
        const { error } = await supabase.auth.updateUser({ password: data.password });
        if (error) throw error;
        callbacks?.onSuccess?.({ success: true });
      } catch (err) {
        const msg = getErrorMessage(err, "Password update failed");
        setErrors({ general: msg });
        callbacks?.onError?.({ general: msg });
      } finally {
        setProcessing(false);
      }
    },
    [data],
  );

  return { data, setData, setFieldData, processing, errors, setErrors, response: null, update };
};

// ---------------------------------------------------------------------------
// useRootPasswordUpdate — Same as above (all users go through Supabase Auth)
// ---------------------------------------------------------------------------

export interface RootPasswordBody {
  password?: string;
  current_password?: string;
  password_confirmation?: string;
}

export const useRootPasswordUpdate = () => {
  const [data, setData] = useState<RootPasswordBody>({});
  const [processing, setProcessing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const setFieldData = useCallback(
    (field: string, value: any) => setData(prev => ({ ...prev, [field]: value })),
    [],
  );

  const update = useCallback(
    async (callbacks?: { onSuccess?: (data: unknown) => void; onError?: (errors: Record<string, string>) => void }) => {
      if (!data.password) { setErrors({ password: "Password required" }); return; }
      setProcessing(true);
      setErrors({});
      try {
        const { error } = await supabase.auth.updateUser({ password: data.password });
        if (error) throw error;
        callbacks?.onSuccess?.({ success: true });
      } catch (err) {
        const msg = getErrorMessage(err, "Password update failed");
        setErrors({ general: msg });
        callbacks?.onError?.({ general: msg });
      } finally {
        setProcessing(false);
      }
    },
    [data],
  );

  return { data, setData, setFieldData, processing, errors, setErrors, response: null, update };
};
